import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, ServerCrash, ExternalLink, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useApi } from "../../hooks/useApi";
import { UrssafConnectLogo } from "./UrssafBrandingKit";

const URSSAF_AE_URL = "https://www.autoentrepreneur.urssaf.fr";

const Header = ({ title, subtitle }) => (
  <div className="flex items-center gap-3 pb-4 border-b">
    <UrssafConnectLogo size={44} />
    <div>
      <DialogTitle className="text-lg">{title}</DialogTitle>
      <DialogDescription className="text-xs">{subtitle}</DialogDescription>
    </div>
  </div>
);

const ConfirmStep = ({ onCancel, onConfirm }) => (
  <div className="space-y-4" data-testid="urssaf-revoke-confirm-step">
    <p className="text-sm">En révoquant le mandat, Biz360 ne pourra plus déclarer ni payer tes cotisations à ta place.</p>
    <ul className="text-sm space-y-1.5 list-disc pl-5 text-muted-foreground">
      <li>Tu devras déclarer toi-même sur ton espace Urssaf.</li>
      <li>Tes ventes, dépenses et ton historique restent dans Biz360.</li>
      <li>Tu peux te reconnecter à tout moment.</li>
    </ul>
    <div className="flex gap-2 pt-2">
      <button type="button" onClick={onCancel} className="flex-1 h-10 rounded-lg border text-sm font-medium hover:bg-muted" data-testid="urssaf-revoke-cancel">Annuler</button>
      <button type="button" onClick={onConfirm} className="flex-1 h-10 rounded-lg bg-destructive text-white text-sm font-medium hover:opacity-90" data-testid="urssaf-revoke-confirm">Confirmer la révocation</button>
    </div>
  </div>
);

const SuccessStep = ({ date, onReconnect, reconnecting, onClose }) => (
  <div className="space-y-4" data-testid="urssaf-revoke-success-step">
    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
      <CheckCircle2 className="text-emerald-600 flex-shrink-0" size={24} />
      <div>
        <div className="font-semibold text-emerald-800">Mandat révoqué</div>
        <div className="text-xs text-emerald-700">Le {date}. L'Urssaf t'envoie un email de confirmation.</div>
      </div>
    </div>
    <div>
      <div className="text-sm font-semibold mb-2">Ce qui change pour toi</div>
      <ul className="text-sm space-y-1.5 list-disc pl-5 text-muted-foreground">
        <li>Biz360 n'est plus ton tiers déclarant : plus de déclaration ni de paiement en 1 clic.</li>
        <li>Tu dois déclarer ton chiffre d'affaires toi-même sur autoentrepreneur.urssaf.fr, avant chaque date limite.</li>
        <li>Ton mandat de prélèvement SEPA reste géré par l'Urssaf, depuis ton espace.</li>
        <li>Tes ventes, dépenses, cotisations prévues et ton historique restent disponibles dans Biz360.</li>
      </ul>
    </div>
    <div className="flex flex-col sm:flex-row gap-2 pt-2">
      <a href={URSSAF_AE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 h-10 rounded-lg border text-sm font-medium hover:bg-muted inline-flex items-center justify-center gap-2" data-testid="urssaf-revoke-goto-urssaf">
        Aller sur mon espace Urssaf <ExternalLink size={14} />
      </a>
      <button type="button" onClick={onReconnect} disabled={reconnecting} className="flex-1 h-10 rounded-lg bg-primary text-primary-foreground text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="urssaf-revoke-reconnect">
        {reconnecting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Me reconnecter
      </button>
    </div>
    <button type="button" onClick={onClose} className="w-full text-xs text-muted-foreground hover:underline" data-testid="urssaf-revoke-close">Fermer</button>
  </div>
);

const ErrorStep = ({ error, onRetry, onClose }) => {
  const isServer = error.status >= 500 || !error.status;
  return (
    <div className="space-y-4" data-testid={isServer ? "urssaf-revoke-error-500" : "urssaf-revoke-error"}>
      <div className={`rounded-lg p-4 flex items-start gap-3 border ${isServer ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
        {isServer ? <ServerCrash className="text-red-600 flex-shrink-0" size={24} /> : <AlertTriangle className="text-amber-600 flex-shrink-0" size={24} />}
        <div>
          <div className={`font-semibold ${isServer ? "text-red-800" : "text-amber-800"}`}>
            {isServer ? "Service Urssaf temporairement indisponible" : "La révocation n'a pas pu être faite"}
          </div>
          <div className="text-sm mt-1 text-foreground/80">
            {isServer
              ? "L'Urssaf n'a pas pu traiter la demande. Ton mandat est toujours actif : rien n'a changé. Réessaie dans quelques minutes."
              : error.message}
          </div>
          {isServer && <div className="text-xs mt-2 text-muted-foreground">Code : {error.status || "réseau"}. Si le problème continue, tu peux révoquer le mandat depuis ton espace Urssaf.</div>}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onClose} className="flex-1 h-10 rounded-lg border text-sm font-medium hover:bg-muted" data-testid="urssaf-revoke-error-close">Fermer</button>
        {isServer && (
          <button type="button" onClick={onRetry} className="flex-1 h-10 rounded-lg bg-primary text-primary-foreground text-sm font-medium inline-flex items-center justify-center gap-2" data-testid="urssaf-revoke-retry">
            <RefreshCw size={14} /> Réessayer
          </button>
        )}
      </div>
    </div>
  );
};

const TITLES = {
  confirm: ["Révoquer mon mandat Urssaf ?", "API Urssaf — annulation du mandat de tierce déclaration"],
  loading: ["Révocation en cours…", "Transmission à l'Urssaf"],
  success: ["C'est fait, ton mandat est révoqué", "Biz360 n'est plus ton tiers déclarant"],
  error: ["Révocation non effectuée", "Réponse de l'Urssaf"],
};

export const UrssafRevokeMandatDialog = ({ open, onOpenChange, onRevoked, onReconnect }) => {
  const api = useApi();
  const [step, setStep] = useState("confirm");
  const [error, setError] = useState(null);
  const [date, setDate] = useState("");
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => { if (open) { setStep("confirm"); setError(null); } }, [open]);

  const revoke = async () => {
    setStep("loading");
    try {
      await api.delete("/urssaf/mandat");
      setDate(new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }));
      setStep("success");
      onRevoked?.();
    } catch (e) {
      const status = e.response?.status;
      const d = e.response?.data?.detail;
      let message = typeof d === "string" ? d : d?.message || "Erreur inconnue";
      if (status === 404) message = "Il n'y a pas de mandat actif à révoquer.";
      if (status === 404) onRevoked?.();
      setError({ status, message });
      setStep("error");
    }
  };

  const reconnect = async () => {
    setReconnecting(true);
    const ok = await onReconnect?.();
    setReconnecting(false);
    if (ok) onOpenChange(false);
  };

  const [title, subtitle] = TITLES[step];
  return (
    <Dialog open={open} onOpenChange={(v) => step !== "loading" && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="urssaf-revoke-dialog">
        <Header title={title} subtitle={subtitle} />
        {step === "confirm" && <ConfirmStep onCancel={() => onOpenChange(false)} onConfirm={revoke} />}
        {step === "loading" && (
          <div className="py-10 flex flex-col items-center gap-3 text-sm text-muted-foreground" data-testid="urssaf-revoke-loading">
            <Loader2 className="animate-spin text-primary" size={32} /> Biz360 transmet ta demande à l'Urssaf…
          </div>
        )}
        {step === "success" && <SuccessStep date={date} onReconnect={reconnect} reconnecting={reconnecting} onClose={() => onOpenChange(false)} />}
        {step === "error" && <ErrorStep error={error} onRetry={revoke} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
};

export default UrssafRevokeMandatDialog;
